/**
 * "Are you sure" as a CRM dialog.
 *
 * Screens used to reach for the shared `ConfirmDialog`, which draws the
 * pre-redesign bordered box: a destructive question in a different shell from
 * every other dialog in the app is exactly the moment the operator should not be
 * wondering which piece of software is asking.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Btn, Modal, type BtnTone } from "../../ui";

export function ConfirmModal({
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  /**
   * The colour of the button that says yes. Red by default, because that is
   * what almost everything behind this dialog is - a delete that does not come
   * back.
   *
   * It is a parameter because a REVERSIBLE step must not wear the same red.
   * Archiving a project asks a question too (the row leaves every list, and the
   * operator cannot see that happen from the row they clicked), but it is
   * undone by one click, and a dialog that looks identical to the delete one
   * teaches operators that red means nothing. The body copy carries the
   * difference in words; this carries it in the half-second before anybody
   * reads them.
   */
  tone?: BtnTone;
  /** Awaited, so the button can stay busy until the record is actually gone. */
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={title} label={title} onClose={onClose} className="max-w-[420px]">
      <p className="crm-sub mt-1">{body}</p>
      <div className="crm-m-foot">
        <Btn type="button" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Btn>
        <Btn type="button" tone={tone} onClick={() => void run()} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {confirmLabel}
        </Btn>
      </div>
    </Modal>
  );
}
